import { type InfiniteData, useInfiniteQuery } from "@tanstack/react-query";
import { type AxiosResponse } from "axios";
import update from "immutability-helper";
import { useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation, useRouteMatch } from "react-router";

import {
    IconMention,
    IconNotifications,
    IconReplyClockwise,
    IconThumbsUp,
} from "@btnet/blueprint-icons";
import { BTTabs } from "@btnet/core-components/BTTabs";
import { BTText } from "@btnet/core-components/BTText";
import { showApixErrorMessage } from "@btnet/services-btnet/apix-handler";
import type { NotificationModel } from "@btnet/services-btnet/handlers/models/notificationModel";
import type { NotificationsByEntitiesListResponse } from "@btnet/services-btnet/handlers/models/notificationsByEntitiesListResponse";
import {
    getNotificationsGroupedNotificationsGetV2QueryKey,
    getNotificationsPanelInfoGetV2QueryKey,
    notificationsGroupedNotificationsGetV2,
    useNotificationsByNotificationIdPatchV2,
    useNotificationsMarkAllReadPutV2,
    useNotificationsPanelInfoPutV2,
} from "@btnet/services-btnet/handlers/notifications/notifications";
import { cn } from "@btnet/utilities-core";
import { useWebviewContext } from "@btnet/utilities-webview";

import { queryClient } from "helpers/queryClient";

import { BTLocalStorage } from "types/btStorage";

import { lazyLoadComponent } from "utilities/LazyLoad.utility";
import { isInPortal } from "utilities/portal/portal";

import { EmptyStateCell } from "commonComponents/entity/emptyState/EmptyStateCell";
import { withErrorBoundary } from "commonComponents/helpers/ErrorBoundary/ErrorBoundary";

import {
    NotificationCardList,
    NotificationCardListSkeleton,
} from "entity/notification/panel/NotificationCardList";
import { NotificationPanelHeader } from "entity/notification/panel/NotificationPanelHeader";
import { NotificationPanelHeaderWebview } from "entity/notification/panel/NotificationPanelHeaderWebview";

import "./NotificationPanel.less";

const NotificationUnfollow = lazyLoadComponent(
    () => import("entity/notification/NotificationUnfollow/NotificationUnfollow"),
);

/**
 * Helper function takes a paginated response and grabs relevant indices for a given notificationId.
 * This is used to update the notification in the local state without having to refetch the entire
 * notifications list, which could disrupt scroll position
 * @param notifications
 * @param notificationId
 * @returns
 */
export function getNotificationIndicesByNotificationId(
    notifications: InfiniteData<AxiosResponse<NotificationsByEntitiesListResponse, unknown>>,
    notificationId: number,
) {
    for (let pageIndex = 0; pageIndex < notifications.pages.length; pageIndex++) {
        const page = notifications.pages[pageIndex];
        for (let entityIndex = 0; entityIndex < page.data.data.length; entityIndex++) {
            const entityNotifications = page.data.data[entityIndex];
            const notificationIndex = entityNotifications.notifications.findIndex(
                (x) => x.id === notificationId,
            );
            if (notificationIndex !== -1) {
                return {
                    pageIndex,
                    entityIndex,
                    notificationIndex,
                };
            }
        }
    }
    return null;
}

interface INotificationPanelProps {
    selectedJobId: number;
    onClose?: () => void;
}

export type NotificationPanelTab = "You" | "General";

export function NotificationPanelInternal({
    selectedJobId,
    onClose,
}: Readonly<INotificationPanelProps>) {
    const isWebview = useWebviewContext() !== undefined;
    const notificationPanelDivRef = useRef<HTMLDivElement>(null);
    const [selectedTab, setSelectedTab] = useState<NotificationPanelTab>(
        BTLocalStorage.get("bt-string-selectedNotificationTab") ?? "You",
    );
    const getListLimit = 20;
    const buttonToFocus = useRef<HTMLButtonElement>(null);
    useEffect(() => {
        if (onClose && buttonToFocus.current) {
            // antd Menu doesnt provide a way to hand off focus to the Panel, so programmatically taking focus using this timeout
            // eslint-disable-next-line @buildertrend/enterprise-extras/no-unhandled-scheduling
            setTimeout(() => {
                if (!notificationPanelDivRef.current?.contains(document.activeElement)) {
                    buttonToFocus.current?.focus({ preventScroll: true });
                }
            }, 200);
        }
    }, [onClose, buttonToFocus]);
    const handleKeyDown = onClose
        ? (e: React.KeyboardEvent) => {
              e.stopPropagation();
              if (e.key === "Escape") {
                  onClose();
              }
          }
        : undefined;
    const mutationErrorOption = {
        mutation: {
            onError: (error: Error) => {
                showApixErrorMessage(error);
            },
        },
    };

    const request = useMemo(() => {
        return {
            IsMentions: selectedTab === "You",
            Limit: getListLimit,
        };
    }, [selectedTab, getListLimit]);

    const notificationQuery = useInfiniteQuery({
        queryKey: getNotificationsGroupedNotificationsGetV2QueryKey(request),
        queryFn: ({ pageParam }) =>
            notificationsGroupedNotificationsGetV2({ ...request, NextCursor: pageParam }),
        refetchOnWindowFocus: false,
        getNextPageParam: (lastPage) => {
            return lastPage.data?.nextCursor ?? null;
        },
        cacheTime: 0,
        staleTime: 0,
    });

    if (notificationQuery.error) {
        throw notificationQuery.error;
    }

    const { mutateAsync: mutatePanelInfoPatchAsync } =
        useNotificationsPanelInfoPutV2(mutationErrorOption);

    const { mutateAsync: mutateNotificationAsync } =
        useNotificationsByNotificationIdPatchV2(mutationErrorOption);

    const { mutateAsync: mutateMarkAllReadAsync } =
        useNotificationsMarkAllReadPutV2(mutationErrorOption);

    const [activeUnfollowNotificationModel, setActiveUnfollowNotificationModel] =
        useState<NotificationModel | null>(null);

    const history = useHistory();
    const match = useRouteMatch();
    const location = useLocation();

    // Update the panel info and refresh data upon initial load - this will cause the bell to indicate panel has been read ie dot goes away
    useEffect(() => {
        (async () => {
            const patchResult = await mutatePanelInfoPatchAsync();
            queryClient.setQueryData(getNotificationsPanelInfoGetV2QueryKey(), patchResult);
        })();
    }, [mutatePanelInfoPatchAsync]);

    const setNotificationAsRead = async (notification: NotificationModel, isRead: boolean) => {
        const patchResult = await mutateNotificationAsync({
            notificationId: notification.id,
            data: { isRead },
        });

        // Update local state without reloading
        if (!notificationQuery.data) {
            return;
        }

        const changedIndices = getNotificationIndicesByNotificationId(
            notificationQuery.data,
            notification.id,
        );

        if (!changedIndices) {
            return;
        }

        const alteredResponse = update(notificationQuery.data, {
            pages: {
                [changedIndices.pageIndex]: {
                    data: {
                        data: {
                            [changedIndices.entityIndex]: {
                                notifications: {
                                    [changedIndices.notificationIndex]: {
                                        dateRead: { $set: patchResult.data.dateRead ?? null },
                                    },
                                },
                            },
                        },
                    },
                },
            },
        });
        queryClient.setQueryData(
            getNotificationsGroupedNotificationsGetV2QueryKey(request),
            alteredResponse,
        );
    };

    const getEmptyStateVerbiage = () => {
        if (isInPortal({ builder: true })) {
            return "Stay on top of your conversations with clients, crew, and the team";
        }
        if (isInPortal({ subs: true })) {
            return "Stay on top of your conversations with the team";
        }
        return "Stay on top of your conversations with your builder";
    };

    const tabContent = (
        <>
            {notificationQuery.isLoading && <NotificationCardListSkeleton />}
            {notificationQuery.data && (
                <NotificationCardList
                    notificationsByEntitiesList={notificationQuery.data.pages.flatMap(
                        (page) => page.data.data,
                    )}
                    selectedJobId={selectedJobId}
                    onLoadMore={notificationQuery.fetchNextPage}
                    hasMore={notificationQuery.hasNextPage ?? false}
                    emptyStateComponent={
                        <div className="NotificationListEmptyStateWrapper">
                            <div className="flex flex-col gap-2 justify-center items-center text-center py-2">
                                <IconNotifications size="lg" />
                                <BTText
                                    className="NotificationListEmptyStateText"
                                    text={
                                        selectedTab === "You"
                                            ? getEmptyStateVerbiage()
                                            : "You don’t have any notifications yet"
                                    }
                                    size="distinct-md"
                                    style={{ lineHeight: "24px", marginBottom: 16 }}
                                />
                            </div>
                            {selectedTab === "You" && (
                                <div className="NotificationListEmptyStateWrapper-Grid">
                                    <EmptyStateCell
                                        color="yellow"
                                        icon={<IconMention />}
                                        text="See when someone’s mentioned you directly"
                                    />
                                    <EmptyStateCell
                                        color="green"
                                        icon={<IconThumbsUp />}
                                        text="Get updates on who’s reacted to your comments"
                                    />
                                    <EmptyStateCell
                                        color="purple"
                                        icon={<IconReplyClockwise />}
                                        text="Look for direct replies to your comments"
                                    />
                                </div>
                            )}
                        </div>
                    }
                    isLoadingMore={notificationQuery.isFetchingNextPage}
                    onNotificationMarkedRead={async (notification, isRead) =>
                        await setNotificationAsRead(notification, isRead)
                    }
                    onNotificationClicked={async (notification) => {
                        if (!notification.dateRead) {
                            await setNotificationAsRead(notification, true);
                        }
                        onClose?.();
                    }}
                    onNotificationUnfollowed={async (notification) => {
                        setActiveUnfollowNotificationModel(notification);
                    }}
                />
            )}
        </>
    );

    const handleClick = onClose
        ? (e: React.MouseEvent) => {
              // click events bubble to the antd Menu.Item, causing it to close
              e.stopPropagation();
          }
        : undefined;

    return (
        <>
            <div
                className={cn("NotificationPanel", {
                    "NotificationPanel--dropdown": !!onClose,
                })}
                onKeyDown={handleKeyDown}
                ref={notificationPanelDivRef}
                onClick={handleClick}
            >
                {isWebview ? (
                    <NotificationPanelHeaderWebview
                        isLoading={notificationQuery.isLoading}
                        onMarkAllReadAsync={async () => {
                            await mutateMarkAllReadAsync();
                            await notificationQuery.refetch();
                        }}
                    />
                ) : (
                    <NotificationPanelHeader
                        selectedJobId={selectedJobId}
                        isLoading={notificationQuery.isLoading}
                        onMarkAllReadAsync={async () => {
                            await mutateMarkAllReadAsync();
                            await notificationQuery.refetch();
                        }}
                        focusButtonRef={buttonToFocus}
                    />
                )}

                <BTTabs
                    className="notification-panel-tabs"
                    data-testid="notification-panel-tabs"
                    activeKey={selectedTab}
                    onChange={(activeKey) => {
                        setSelectedTab(activeKey as NotificationPanelTab);
                        BTLocalStorage.set(
                            "bt-string-selectedNotificationTab",
                            activeKey as NotificationPanelTab,
                        );
                    }}
                >
                    <BTTabs.TabPane key="You" tab={<BTText text="You" size="normal-md" />}>
                        {tabContent}
                    </BTTabs.TabPane>
                    <BTTabs.TabPane key="General" tab={<BTText text="General" size="normal-md" />}>
                        {tabContent}
                    </BTTabs.TabPane>
                </BTTabs>
            </div>
            {activeUnfollowNotificationModel && (
                <NotificationUnfollow
                    builderId={activeUnfollowNotificationModel.orgId}
                    history={history}
                    location={location}
                    match={match}
                    notificationId={activeUnfollowNotificationModel.id}
                    modalConfig={{
                        beforeClose: () => {
                            setActiveUnfollowNotificationModel(null);
                        },
                        parentRoute: match.url,
                    }}
                />
            )}
        </>
    );
}

export const NotificationPanel = withErrorBoundary(NotificationPanelInternal)(
    "Could not load notification center",
);
export default NotificationPanel;
